// src/pages/auth/ForgotPassword.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Clock } from 'lucide-react';

const ForgotPassword = () => {
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [emailServiceIssue, setEmailServiceIssue] = useState(false);
  const [otpForTesting, setOtpForTesting] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email is required');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setEmailServiceIssue(false);
    setOtpForTesting('');

    try {
      const response = await fetch('https://medigo-api.onrender.com/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim() })
      });

      const data = await response.json();
      
      if (data.success) {
        // Check if email service failed but OTP was generated
        if (data.emailSent === false && data.emailServiceError) {
          setEmailServiceIssue(true);
          if (data.otp) {
            setOtpForTesting(data.otp);
          }
          setIsSuccess(true);
        } 
        // Email sent successfully
        else if (data.emailSent === true) {
          setIsSuccess(true);
          // Auto-redirect to verify-otp page after 2 seconds
          setTimeout(() => {
            navigate(`/auth/verify-otp?email=${encodeURIComponent(email)}`);
          }, 2000);
        }
        // Generic success response
        else {
          setIsSuccess(true);
          // Still redirect in case they have the OTP
          setTimeout(() => {
            navigate(`/auth/verify-otp?email=${encodeURIComponent(email)}`);
          }, 2000);
        }
      } else {
        setError(data.message || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Success state with OTP display (if email service failed)
  if (isSuccess && emailServiceIssue && otpForTesting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Service Issue</h1>
            <p className="text-gray-600">
              Password reset code generated but email service is temporarily unavailable.
            </p>
          </div>

          {/* Development Mode OTP Display */}
          <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
            <p className="text-sm font-semibold text-yellow-800 mb-2">
              🔐 Development Mode - Your OTP Code:
            </p>
            <div className="bg-white rounded p-3 mb-3">
              <p className="text-3xl font-mono font-bold text-yellow-900 text-center tracking-wider">
                {otpForTesting}
              </p>
            </div>
            <p className="text-xs text-yellow-700">
              ⏰ This code expires in 15 minutes. Copy it and use it on the next page.
            </p>
          </div>

          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-semibold text-blue-900 mb-2">Next steps:</p>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal ml-4">
              <li>Copy the OTP code shown above</li>
              <li>Click "Continue to Verify OTP" below</li>
              <li>Enter the OTP code to verify</li>
              <li>Then set your new password</li>
            </ol>
          </div>

          <button
            onClick={() => navigate(`/auth/verify-otp?email=${encodeURIComponent(email)}`)}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors mb-4"
          >
            Continue to Verify OTP →
          </button>

          <div className="text-center">
            <Link
              to="/auth/login"
              className="text-blue-600 hover:text-blue-500 font-medium flex items-center justify-center"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Normal success state (email sent)
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h1>
            <p className="text-gray-600 mb-4">
              We've sent a 6-digit verification code to <strong>{email}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Redirecting to verification page...
            </p>
            
            {/* Manual navigation option */}
            <button
              onClick={() => navigate(`/auth/verify-otp?email=${encodeURIComponent(email)}`)}
              className="text-blue-600 hover:text-blue-500 font-medium"
            >
              Click here if not redirected
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Forgot Password?</h1>
          <p className="text-gray-600 mt-2">
            Enter your email and we'll send you a verification code
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your email address"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              'Send Verification Code'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/auth/login"
            className="text-blue-600 hover:text-blue-500 font-medium flex items-center justify-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </Link>
        </div>

        {/* Help Section */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            What to expect:
          </h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• You'll receive a 6-digit verification code</li>
            <li>• The code expires in 15 minutes</li>
            <li>• Check your spam folder if you don't see the email</li>
            <li>• You can request a new code after 2 minutes</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;